import { Option } from '@app/types';
import {
  Box,
  classSet,
  InputIcon,
  Portal,
  useRect
} from '@platform/ui-library';
import { InputIconProps } from '@platform/ui-library/build/InputIcon/types';
import {
  FC,
  SyntheticEvent,
  useEffect,
  useState
} from 'react';
import styles from './autocomplete.module.scss';

const DEFAULT_MIN_LENGTH = 3;

type AutocompleteBoldedOption<T> = (
  Option<T>
  & {
    boldSubstring: string;
    firstSubstring: string;
    lastSubstring: string;
  }
);

export type AutocompleteProps<T> = (
  {
    /** Whether the input that the user can type in is disabled. */
    disabled?: boolean;
    /**
     * Label for the empty option at the top of the dropdown.
     *
     * @note If not provided, no empty option is shown.
     */
    emptyLabel?: string;
    /** Gets options that should be displayed for the current @see searchValue. */
    getOptions: (searchValue: string) => (
      Option<T>[]
      | Promise<Option<T>[]>
    );
    /** Replace the default labels. */
    labels?: Partial<{
      /** Displayed while waiting for @see getOptions to resolve. */
      isLoading: string;
      /** Displayed when @see getOptions does not return options. */
      notFound: string;
      /** Displayed at the top of the dropdown options. */
      title: string;
    }>;
    /** Minimum length @see searchValue has to be before @see getOptions is called @default DEFAULT_MIN_LENGTH. */
    minSearchLength?: number;
    /** Called when an option is selected from the dropdown. */
    onValueChange: (value: T) => void;
    /** Whether the input that the user can type in is required. */
    required?: boolean;
    /** Selected option. */
    value: T | null | undefined;
  } & (
    // Parent component controls the autocomplete's search.
    {
      initSearchValue?: never;
      /** Called when the user types in the search input. */
      onSearchValueChange: (searchValue: string) => void;
      /** Value displayed in the search input. */
      searchValue: string;
    }
    // Autocomplete controls its own search value.
    | {
      /** Search value to start with. */
      initSearchValue?: string;
      onSearchValueChange?: never;
      searchValue?: never;
    }
  ) & Pick<InputIconProps, 'autoFocus' | 'icon' | 'label' | 'placeholder'>
);

export function Autocomplete<T>({
  disabled,
  emptyLabel,
  getOptions,
  initSearchValue,
  labels,
  minSearchLength = DEFAULT_MIN_LENGTH,
  onSearchValueChange,
  onValueChange,
  searchValue: propsSearchValue,
  value,
  ...inputProps
}: AutocompleteProps<T>): ReturnType<FC> {
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<Option<T>[]>([]);
  const [stateSearchValue, setStateSearchValue] = useState(
    initSearchValue ?? emptyLabel ?? ''
  );
  const searchValue = (propsSearchValue ?? stateSearchValue);
  const setSearchValue = onSearchValueChange ?? setStateSearchValue;
  const [containerRef, bbox, updateContainerBbox] = useRect();

  useEffect(() => {
    if (
      isFocused
      && (searchValue.length ?? 0) >= minSearchLength
    ) {
      (async () => {
        setIsLoading(true);
        setIsOpen(true);
        setOptions(await getOptions(searchValue));
        setIsLoading(false);
      })();
    } else if (isOpen) {
      setIsOpen(false);
    }
  }, [
    getOptions,
    isFocused,
    minSearchLength,
    searchValue
  ]);

  useEffect(() => {
    if (isOpen) {
      updateContainerBbox();
    }
  }, [isOpen, disabled]);

  useEffect(() => {
    if (!isFocused && !value) {
      setSearchValue(emptyLabel ?? '');
    }
  }, [isFocused, value]);

  const Content = (): JSX.Element => {
    if (isLoading) {
      return (
        <p className={styles.message}>{labels?.isLoading ?? 'Loading results, please wait...'}</p>
      );
    } else if (!options.length) {
      return (
        <p className={styles.message}>{labels?.notFound ?? 'The item you are looking for does not exist.'}</p>
      );
    }

    const isSearchSelected = options.some((option) =>
      option.value === value
      && option.label === searchValue
    );
    let boldOptions = options.map((item): AutocompleteBoldedOption<T> => {
      const { label } = item;
      const match = new RegExp(
        // Escape special characters like '*' that breaks the search.
        searchValue.replace(/([^\s\w])/g, '\\$1'),
        'i'
      ).exec(label)!;
      const startIndexForBoldText = !match ? 0 : match.index;
      const endIndexForBoldText = !match ? 0 : match.index + match[0].length;
      const firstSubstring = label.substring(0, startIndexForBoldText);
      const boldSubstring = label.substring(startIndexForBoldText, endIndexForBoldText);
      const lastSubstring = label.substring(endIndexForBoldText);
      return {
        ...item,
        firstSubstring,
        boldSubstring,
        lastSubstring
      };
    }).sort((a, b) =>
      // Sort options to dislay the ones with the least characters before the search first.
      (!searchValue) ? 0 : a.firstSubstring.length - b.firstSubstring.length
    );

    if (emptyLabel && (!searchValue || isSearchSelected)) {
      boldOptions = [{
        boldSubstring: '',
        firstSubstring: emptyLabel,
        label: emptyLabel,
        lastSubstring: '',
        value: '' as unknown as T
      }, ...boldOptions];
    }

    return (
      <div className={styles.list}>
        {boldOptions.map(({
          icon,
          id,
          label,
          value: optionValue,
          firstSubstring,
          boldSubstring,
          lastSubstring
        }) => {
          return (
            <button
              className={(optionValue == value) ? 'selected' : ''}
              key={id ?? label}
              onMouseDown={() => {
                requestAnimationFrame(() => {
                  setIsOpen(false);
                  setSearchValue(label);
                  onValueChange(optionValue);
                });
              }}
              title={label}
              type="button"
            >
              {icon && (typeof icon === 'string' ? <i className={`fe icon-${icon}`}></i> : <>{icon}</>)}
              <span>{firstSubstring}<strong>{boldSubstring}</strong>{lastSubstring}</span>
              <i className="fe icon-chevron-right" />
            </button>
          );
        })}
      </div>
    );
  };

  const boxStyle = {
    left: bbox.left + 'px',
    top: bbox.bottom + 'px',
    width: bbox.width + 'px'
  };
  const useHeader = labels?.title !== '';

  return (
    <div ref={containerRef}>
      <InputIcon
        disabled={disabled}
        onBlur={() => {
          setIsFocused(false);
          setIsOpen(false);
        }}
        onChange={(e: SyntheticEvent<HTMLInputElement>) => {
          const newSearchValue = e.currentTarget.value;
          setSearchValue(newSearchValue);
          if (emptyLabel && value) {
            onValueChange('' as unknown as T);
          }
        }}
        onClick={(e: any) => {
          e.target?.select?.();
        }}
        onFocus={(e) => {
          setIsFocused(true);
          e.target.select();
        }}
        value={searchValue}
        {...inputProps}
      />
      {isOpen && !disabled && (
        <Portal>
          <Box
            className={classSet([styles.autocompleteBox, !useHeader && styles.noHeader])}
            theme="noPadding"
            style={boxStyle}
          >
            {useHeader && (
              <div className={styles.header}>
                <p className={styles.subtitle}>{labels?.title ?? 'Search results:'}</p>
                <i className="fe icon-x" />
              </div>
            )}
            <Content />
          </Box>
        </Portal>
      )}
    </div>
  );
}
